import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import logger from '../utils/logger';

interface SwipeNavigationOptions {
  enabled?: boolean;
  threshold?: number; // Minimum swipe distance in pixels
  swipeVelocity?: number; // Minimum swipe velocity
  preventDefaultOnSwipe?: boolean;
  onSwipeBack?: () => void;
  onSwipeForward?: () => void;
}

/**
 * Hook for enabling swipe navigation gestures
 * Supports swipe-back (right) and swipe-forward (left) navigation
 */
export const useSwipeNavigation = (options: SwipeNavigationOptions = {}) => {
  const {
    enabled = true,
    threshold = 50,
    swipeVelocity = 0.3,
    preventDefaultOnSwipe = false,
    onSwipeBack,
    onSwipeForward
  } = options;

  const navigate = useNavigate();
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const swipeInProgress = useRef<boolean>(false);

  useEffect(() => {
    // Only enable on mobile platforms and when enabled
    const platform = Capacitor.getPlatform();
    if (!enabled || platform === 'web') {
      return;
    }

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      
      // Ignore touches in bottom nav area (bottom 80px)
      const screenHeight = window.innerHeight;
      if (touch.clientY > screenHeight - 80) {
        swipeInProgress.current = false;
        return;
      }

      // Only track if starting from edge
      const screenWidth = window.innerWidth;
      if (touch.clientX < 20 || touch.clientX > screenWidth - 20) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        touchStartTime.current = Date.now();
        swipeInProgress.current = true;
      } else {
        swipeInProgress.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!swipeInProgress.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Check if horizontal swipe (deltaX > deltaY)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        if (preventDefaultOnSwipe) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!swipeInProgress.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;
      const deltaTime = Date.now() - touchStartTime.current;
      const velocity = Math.abs(deltaX) / deltaTime;

      // Check if horizontal swipe with sufficient distance and velocity
      if (
        Math.abs(deltaX) > Math.abs(deltaY) && 
        Math.abs(deltaX) > threshold && 
        velocity > swipeVelocity
      ) {
        if (deltaX > 0 && touchStartX.current < 20) {
          // Swipe right from left edge - go back
          logger.info('Swipe back detected');
          if (onSwipeBack) {
            onSwipeBack();
          } else {
            navigate(-1);
          }
        } else if (deltaX < 0 && touchStartX.current > window.innerWidth - 20) {
          // Swipe left from right edge - go forward
          logger.info('Swipe forward detected');
          if (onSwipeForward) {
            onSwipeForward();
          } else {
            navigate(1);
          }
        }
      }

      swipeInProgress.current = false;
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: !preventDefaultOnSwipe });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Cleanup
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, threshold, swipeVelocity, preventDefaultOnSwipe, navigate, onSwipeBack, onSwipeForward]);
};

export default useSwipeNavigation;